/* External */
import { call, put, all, takeLatest } from "redux-saga/effects";
import { addSeconds } from "date-fns";
import jwt_decode from "jwt-decode";

/* Actions */
import * as actions from "./actions";
import { updateUser } from "../user/actions";

/* Services */
import { loginWithForgeRock as loginService, federatedLogin as federatedLoginService } from "./services";
import { backendService, forgeRockService } from "../../services";

/* Others */
import store from "../../state/configureStore";
import { history } from "../../../src/index.js";
import {
  clearLocalStorage,
  getUser as getUserFromLS,
  SESSION_TOKEN,
  RENEW_ENTITLEMENTS,
  FR_ACCESS_TOKEN,
  FR_ID_TOKEN,
  USER
} from "../../state/localStorage";
import {
  checkCookie,
  getTokenDecoded,
  getUserTypeByBusinessUnit,
  isLocalLoginEnabled,
  setCookie,
  toUnixTimestamp,
  USER_TYPES
} from "../../utils";

function* login({ payload }) {
  yield put(actions.loginRequest());
  try {
    const refreshTokenResponse = yield call(loginService, payload.userName, payload.password);
    const { data: { expires_in = 0, refresh_token = "", id_token = "", access_token = "" } = {} } =
      refreshTokenResponse;
    const deviceId = (+new Date() + Math.random() * 100).toString(32);
    const { data: federatedLoginResponse } = yield call(federatedLoginService, id_token, deviceId);
    const { businessUnit, msoProvider } = jwt_decode(id_token);
    const convivaAccountType = getUserTypeByBusinessUnit(businessUnit, msoProvider);
    const modifiedFederatedResponse = {
      ...federatedLoginResponse,
      account: {
        ...federatedLoginResponse.account,
        userType: convivaAccountType,
        isTVE: convivaAccountType === USER_TYPES.TVE,
        deviceId
      }
    };
    const renewedEntitlements = {
      refresh_token,
      expireDate: toUnixTimestamp(addSeconds(new Date(), expires_in))
    };

    localStorage.setItem(SESSION_TOKEN, modifiedFederatedResponse.sessionToken);
    localStorage.setItem(RENEW_ENTITLEMENTS, JSON.stringify(renewedEntitlements));
    localStorage.setItem(FR_ACCESS_TOKEN, access_token);
    localStorage.setItem(FR_ID_TOKEN, id_token);

    store.dispatch(updateUser("renewEntitlements", renewedEntitlements));
    store.dispatch(updateUser("frToken", access_token));
    store.dispatch(updateUser("frIdToken", id_token));
    store.dispatch(updateUser("sessionToken", modifiedFederatedResponse.sessionToken));
    store.dispatch(updateUser("account", modifiedFederatedResponse.account));
    msoProvider && store.dispatch(updateUser("account.msoProvider", msoProvider.trim()));
    store.dispatch(updateUser("deviceId", deviceId));
    yield put(actions.loginSuccess());
    const { redirectTo } = payload;
    if (redirectTo) return history.push(redirectTo);
  } catch (error) {
    console.error(`userSaga-login: ${error}`);
    yield put(actions.loginFailure(error));
  }
}

function* getUserPreferences() {
  try {
    const { data: { account: { entitlements } = {} } = {} } = yield call(backendService.subscriber.getUserProfile);
    const currentUserLS = getUserFromLS();
    const currentValidEntitlements = entitlements.filter((entitlement) => entitlement.entitled);
    const entitlementsArray = currentValidEntitlements
      .map((validEntitlement) => validEntitlement?.package?.customData?.labels)
      .filter((entitlementLabel) => !!entitlementLabel);
    const entitlementsStringArray = entitlementsArray.map((element) => String(element));
    const updatedUserLS = { ...currentUserLS, entitlements, entitlementsLabels: entitlementsStringArray };
    localStorage.setItem(USER, JSON.stringify(updatedUserLS));
    const { businessUnit, msoProvider } = getTokenDecoded(FR_ID_TOKEN);
    const convivaAccountType = getUserTypeByBusinessUnit(businessUnit, msoProvider);

    yield put(
      actions.setUser({
        account: {
          entitlements,
          entitlementsLabels: entitlementsStringArray,
          userType: convivaAccountType
        },
        hasUserPreferencesBeenFetched: true
      })
    );
    if (msoProvider) {
      yield put(actions.updateUser("account.msoProvider", msoProvider.trim()));
    }
  } catch (error) {
    console.error(`userSaga-getUserPreferences: ${error}`);
    yield put(actions.loginFailure(error));
  }
}

function setUser({ payload }) {
  !checkCookie("user_session_token") && setCookie("user_session_token", payload.sessionToken, 2);
}

function* updateInfoUser({ payload }) {
  const { infoToChange, property, value } = payload;
  try {
    delete infoToChange.email;
    yield call(backendService.subscriber.updateUser, infoToChange);
    yield put(actions.updateUser(property, value));
  } catch (error) {
    console.error(`userSaga-updateInfoUser:`, error);
  }
}

function* logout() {
  try {
    yield call(forgeRockService.login.logout);
  } catch (error) {
    console.error(`userSaga-logout: ${error}`);
  } finally {
    clearLocalStorage();
    window.location.replace(isLocalLoginEnabled ? "/mobile/login" : "/");
  }
}

export default function* root() {
  yield all([
    takeLatest(actions.ACTION_TYPES.GET_USER_PREFERENCES, getUserPreferences),
    takeLatest(actions.ACTION_TYPES.LOGIN, login),
    takeLatest(actions.ACTION_TYPES.LOGOUT, logout),
    takeLatest(actions.ACTION_TYPES.UPDATE_INFO_USER, updateInfoUser),
    takeLatest(actions.ACTION_TYPES.SET_USER, setUser)
  ]);
}
